import React from "react";
import { useTranslation } from "react-i18next";
import { UxSizeEnum, UxIcon, UxIconEnum } from "@imaginelearning/mps-ux-component-library";
import EdgeBackButton from "../edge-back-button/edge-back-button";
import useBreakpoint from "../liftoff/hooks/useBreakpoint";
import "./small-screen-panel.scss";

export interface SmallScreenPanelProps {
  left?: React.ReactNode;
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onBackClick?: () => void;
  /** When true, renders the component as a footer positioned at the bottom of the screen and prevents back button from showing */
  isFooter?: boolean;
  /** Callback function for the back to current control when isFooter is true */
  onBackToCurrentClick?: () => void;
}

function SmallScreenPanel({
  left,
  right,
  children,
  className = "",
  onBackClick,
  isFooter = false,
  onBackToCurrentClick
}: SmallScreenPanelProps) {
  const { isSmallScreen } = useBreakpoint();
  const { t } = useTranslation();

  if (!isSmallScreen) return null;

  const combinedClassName = `small-screen-subheader ${isFooter ? "small-screen-footer" : ""} ${className}`.trim();

  // Render Back to Current control when isFooter is true and callback is provided
  const backToCurrentControl =
    isFooter && onBackToCurrentClick ? (
      <div
        role="button"
        tabIndex={0}
        onClick={onBackToCurrentClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onBackToCurrentClick();
          }
        }}
        className="back-to-current-control"
      >
        <div className="back-to-current-content">
          <span className="back-to-current-text">{t("Back to Current")}</span>
          <UxIcon icon={UxIconEnum.ARROW_LEFT} size={UxSizeEnum.LARGE} className="back-to-current-icon" />
        </div>
      </div>
    ) : null;

  return (
    <div className={combinedClassName}>
      <div className="small-screen-subheader-left">
        {!isFooter && onBackClick && <EdgeBackButton iconSize={UxSizeEnum.MEDIUM} onBackClick={onBackClick} />}
        {left}
      </div>
      <div className="small-screen-subheader-center">{children}</div>
      <div className="small-screen-subheader-right">
        {backToCurrentControl}
        {right}
      </div>
    </div>
  );
}

export default SmallScreenPanel;
